import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Expand, X } from "lucide-react";
import { EASE, Item, Stagger } from "@/components/motion/Reveal";

const SPANS = ["sm:col-span-2 sm:row-span-2", "", "", "", ""];

export const Gallery = ({ images }) => {
  const [idx, setIdx] = useState(null);
  const open = idx !== null;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setIdx(null);
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, images.length]);

  return (
    <>
      <Stagger className="grid auto-rows-[220px] gap-3 sm:grid-cols-4" gap={0.08} data-testid="vehicle-gallery">
        {images.map((img, i) => (
          <Item key={img.src} className={`group relative overflow-hidden bg-ink ${SPANS[i] || ""}`}>
            <button onClick={() => setIdx(i)} className="absolute inset-0 h-full w-full" aria-label={`Apri foto ${i + 1}`} data-testid={`gallery-item-${i}`}>
              <img src={img.src} alt={img.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
              <span className="absolute inset-0 bg-ink/0 transition-colors duration-500 group-hover:bg-ink/30" />
              <span className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center border border-white/40 bg-ink/50 text-white opacity-0 backdrop-blur transition-opacity duration-300 group-hover:opacity-100">
                <Expand className="h-4 w-4" />
              </span>
            </button>
          </Item>
        ))}
      </Stagger>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/95 p-4 backdrop-blur-sm sm:p-10"
            onClick={() => setIdx(null)}
            data-testid="gallery-lightbox"
            data-lenis-prevent
          >
            <button className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center border border-white/20 text-white hover:bg-white/10" aria-label="Chiudi" data-testid="gallery-close">
              <X className="h-5 w-5" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + images.length) % images.length); }} className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-white/20 text-white hover:bg-white/10 sm:left-6" aria-label="Precedente" data-testid="gallery-prev">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % images.length); }} className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-white/20 text-white hover:bg-white/10 sm:right-6" aria-label="Successiva" data-testid="gallery-next">
              <ChevronRight className="h-5 w-5" />
            </button>
            <motion.img
              key={images[idx].src}
              src={images[idx].src}
              alt={images[idx].alt}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="max-h-full max-w-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="absolute bottom-5 left-1/2 -translate-x-1/2 font-mono text-[11px] uppercase tracking-widest text-white/60">
              {idx + 1} / {images.length} — {images[idx].alt}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
